import { useSyncExternalStore } from "react";

/**
 * Iframe-side highlight state for select mode, consumed by
 * `SelectModeSelectable` to draw its own purple border when it is the
 * hovered or committed selection.
 *
 * Why this lives in the iframe rather than the agent-ui parent overlay:
 * the parent only learns the *type* of the current selection
 * (`agentUiState.selectionType`), never which selectable it is, and a
 * parent-drawn box would have to be re-projected through the iframe
 * offset on every scroll/resize. Drawing the border on the selectable
 * itself keeps it pixel-accurate and scroll-native, the same reasoning
 * canopy uses for its in-iframe selection frames.
 *
 * Keys are the raw `data-replo-selectable-payload` strings — the exact
 * value `SelectModeSelectable` writes and the `SelectableDetector` reads
 * back — so both sides agree on identity without re-serializing (which
 * could reorder object keys and break equality).
 */
let selectedKeys: ReadonlySet<string> = new Set();
let hoveredKey: string | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setSelectedSelectableKeys(keys: string[]) {
  selectedKeys = new Set(keys);
  emit();
}

export function setHoveredSelectableKey(key: string | null) {
  if (hoveredKey === key) {
    return;
  }
  hoveredKey = key;
  emit();
}

export function clearSelectionHighlights() {
  if (selectedKeys.size === 0 && hoveredKey === null) {
    return;
  }
  selectedKeys = new Set();
  hoveredKey = null;
  emit();
}

export function useIsSelectableSelected(key: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => selectedKeys.has(key),
    () => false,
  );
}

export function useIsSelectableHovered(key: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => hoveredKey === key,
    () => false,
  );
}
